/**
 * @module composables/useSearchEpisodes
 *
 * Composable for managing episode search state within a specific podcast.
 * Provides search query, download status filter, and search execution state.
 * Used by ListEpisodesView and ListLatestView for filtering episodes
 * within their respective contexts.
 *
 * All refs are module-scoped singletons so search state persists when
 * navigating between the search UI and other components.
 */

import { ref, computed } from "vue";
import { useTags } from "./useTags.js";

/** Controls visibility of the search input panel */
const showSearch = ref(false);

/** Whether a search has been executed (for showing reset/clear action) */
const hasSearched = ref(false);

/** The current search query text */
const searchQuery = ref("");

/** Download status filter (e.g., "downloaded", "downloading", "starred", "saved", or null for all) */
const download_status = ref(null);

/**
 * Tag filter: the id of the tag selected in the search dialog's tag field
 * (null = no tag filter). Episodes must carry this tag to match. Server-side
 * views send it as the `tag` query param; Playlist/History filter locally
 * against the useTags assignment map.
 */
const selectedTagId = ref(null);

/** Trigger flag to execute a search from an external component */
const executeSearch = ref(false);

// Tag list (shared singleton) — used to resolve the selected tag's name for
// the human-readable filter description below
const { tags: allTags } = useTags();

/**
 * Computed validation: search can execute when the query has at least
 * 3 characters OR a download status filter is set OR a tag is selected.
 * Null means no filter selected.
 */
const canSearch = computed(
  () =>
    (searchQuery.value && searchQuery.value.length >= 3) ||
    download_status.value !== null ||
    selectedTagId.value !== null,
);

/**
 * Human-readable labels for the status filter values — used when describing
 * the active filter (e.g. in the app-bar funnel tooltip). Mirrors the
 * statusOptions list in SearchEpisodesDialog / SearchView.
 */
const STATUS_LABELS = {
  downloaded: "Cached",
  downloading: "Downloading",
  starred: "Starred",
  saved: "Saved",
  annotated: "Annotated",
};

/**
 * One-line human-readable description of the currently active search filter,
 * e.g. `Filtered: “star” · Starred`. Shown in the app-bar funnel tooltip so
 * the user is reminded WHAT they filtered by before tapping to clear it.
 * Falls back to a generic label if the state is somehow empty while active.
 */
const searchDescription = computed(() => {
  const parts = [];
  // The text query (quoted, so it reads as the thing that was typed)
  if (searchQuery.value && searchQuery.value.length >= 3) {
    parts.push(`“${searchQuery.value}”`);
  }
  // The status filter, translated to its dropdown label
  if (download_status.value) {
    parts.push(STATUS_LABELS[download_status.value] || download_status.value);
  }
  // The tag filter, shown as #name (resolved from the shared tag list)
  if (selectedTagId.value !== null) {
    const tag = allTags.value.find((t) => t.id === selectedTagId.value);
    parts.push(tag ? `#${tag.name}` : "#tag");
  }
  return parts.length > 0 ? `Filtered: ${parts.join(" · ")}` : "Filter active";
});

/**
 * Reset all search state to defaults.
 * Clears the query, download status, and tag filter, and marks search inactive.
 */
const resetSearchEpisodes = async () => {
  hasSearched.value = false;
  searchQuery.value = null;
  download_status.value = null;
  selectedTagId.value = null;
};

/**
 * Composable function returning all episode search state and functions.
 *
 * @returns {Object} Search state refs, computed properties, and reset function
 */
export function useSearchEpisodes() {
  return {
    executeSearch,
    canSearch,
    showSearch,
    hasSearched,
    searchQuery,
    resetSearchEpisodes,
    download_status,
    selectedTagId,
    searchDescription,
    //
  };
}
